import clsx from 'clsx';
import React from 'react';
import styles from '../RevenueVendorExpenseTable.module.scss';
import dropdownBlack from '../../../../../../assets/dropdown-black.svg';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import {
    overlapInfoIconGrey,
    overlapInfoIconYellow,
    overlapInfoIconOrange,
    overlapInfoIconLightRed,
    overlapInfoIconDarkRed,
} from '../../../../../../assets/hostedassets';
import {
    CIRCULAR_TXN_KEY,
    DEFAULT_OPEN_GROUP,
    MSME_DELAY_PAN_KEY,
    NET_KEY,
    OVERLAP_TAB_CONFIG,
    OVERLAP_TOOLTIP_HIDE_DELAY,
    OpenGroup,
    OverlapRow,
    OverlapTabConfig,
    ROW_TOOLTIPS,
    circularRevenuePct,
    circularSeverity,
    formatCircularPct,
    formatWholeRupeesINR,
    getNetValue,
    isNegativeAmount,
    isOverlappingRow,
    isEmptyGroupRow,
    isGroupRow,
    isSummaryRow,
    parseINR,
    scaleINR,
} from './overlap.constants';
import OverlapTooltipContent from './OverlapTooltipContent';
import IntraTooltipContent from './IntraTooltipContent';
import MsmeDelayTag from './MsmeDelayTag';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_GST_OVERLAP_FLAG_CLICKED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';

// Row height (px) — matches .TableBodyRowItem height: 2.75rem. Used to stack the
// bottom-pinned summary rows and the sticky group bars.
const ROW_HEIGHT_PX = 44;

const SEVERITY_CLASS = {
    yellow: styles.CircularYellow,
    orange: styles.CircularOrange,
    lightRed: styles.CircularLightRed,
    darkRed: styles.CircularDarkRed,
};

// Per-severity hosted info icon — each PNG has the matching badge text colour baked in
// (a hosted <img> can't inherit currentColor). The dark-red PNG is #9e3b3b (correct on the
// white-bg summary text); on the filled dark pill a CSS filter whitens it
// (.CircularBadge.CircularDarkRed .OverlapInfoIcon)
const SEVERITY_ICON = {
    yellow: overlapInfoIconYellow,
    orange: overlapInfoIconOrange,
    lightRed: overlapInfoIconLightRed,
    darkRed: overlapInfoIconDarkRed,
};

// Severity-coloured % badge with an in-badge info icon. Used for Circular Txn.
// (per customer) and Intra Company Txn. (per month). `pill` = coloured pill vs plain coloured text.
// The info icon is the per-severity hosted PNG (SEVERITY_ICON), matching the badge colour.
function PctBadge({
    pct,
    tooltip,
    pill = true,
    rich = false,
    type,
}: {
    pct?: string;
    tooltip?: React.ReactNode; // omitted → badge renders without the in-badge info icon
    pill?: boolean;
    rich?: boolean;
    type: 'circular' | 'intra';
}) {
    return (
        <span className={styles.CircularBadgeCell}>
            <span className={clsx(pill ? styles.CircularBadge : styles.CircularPctText, SEVERITY_CLASS[circularSeverity(pct)])}>
                {formatCircularPct(pct)}
                {tooltip != null && (
                    <Tooltip
                        content={tooltip}
                        direction="top"
                        usePortal
                        fitContent
                        hideArrow={rich}
                        hideDelay={OVERLAP_TOOLTIP_HIDE_DELAY}
                        contentClassName={rich ? styles.OverlapTooltipContent : undefined}
                    >
                        <img
                            src={SEVERITY_ICON[circularSeverity(pct)]}
                            alt="info"
                            className={styles.OverlapInfoIcon}
                            onClick={() => Mixpanel.track(AICA_GST_OVERLAP_FLAG_CLICKED, { type })}
                        />
                    </Tooltip>
                )}
            </span>
        </span>
    );
}

interface RowsProps {
    rows: any;
    headers: any;
    tableRef: any;
    tabConfig?: OverlapTabConfig;
    openGroup?: OpenGroup;
    onToggleGroup?: (group: 'customers' | 'related') => void;
    unitDivisor?: number;
}

function Rows(props: RowsProps) {
    const { rows, headers, tabConfig = OVERLAP_TAB_CONFIG.revenue, openGroup = DEFAULT_OPEN_GROUP, onToggleGroup, unitDivisor = 1 } = props;
    // First summary row (Total B2B Revenue) gets a darker separator above it (per Figma).
    const firstSummaryIdx = Array.isArray(rows) ? rows.findIndex((r) => isSummaryRow(r)) : -1;
    const summaryCount = Array.isArray(rows) ? rows.filter((r) => isSummaryRow(r)).length : 0;
    return (
        <>
            {rows?.map?.((row, i) => {
                const overlapRow: OverlapRow = row;
                const summary = isSummaryRow(overlapRow);
                const group = isGroupRow(overlapRow);
                const emptyGroup = isEmptyGroupRow(overlapRow);
                // Group bars pin under the header (stacked) and above the summary block (stacked).
                const groupIndex = overlapRow.__group === 'customers' ? 0 : 1;
                const groupStyle = group
                    ? { top: `${(groupIndex + 1) * ROW_HEIGHT_PX}px`, bottom: `${(summaryCount + 1 - groupIndex) * ROW_HEIGHT_PX}px` }
                    : undefined;
                // Summary rows are pinned at the bottom; stack them by their distance from the last row.
                const rowCount = rows?.length || 0;
                const bottomOffset = summary ? (rowCount - 1 - i) * ROW_HEIGHT_PX : undefined;
                // Group bars change index when the accordion switches; a stable key keeps the focused toggle mounted.
                const rowKey = overlapRow.__group ?? (emptyGroup ? 'related-empty' : i);
                return (
                    <tr
                        key={rowKey}
                        className={clsx(styles.TableBodyRow, {
                            [styles.SummaryRow]: summary,
                            [styles.IntraRow]: !!overlapRow?.__isIntra,
                            [styles.GroupRow]: group,
                            [styles.EmptyGroupRow]: emptyGroup,
                            [styles.SummaryTop]: i === firstSummaryIdx,
                            [styles.NetB2bRow]: overlapRow?.name === tabConfig.netRowName,
                        })}
                    >
                        {headers?.map((header, hIdx) => {
                            const key = header?.key;
                            const isCircular = key === CIRCULAR_TXN_KEY;
                            const isNet = key === NET_KEY;
                            const isName = hIdx === 0; // Customer Name (first) column
                            const value = row?.[key];

                            let content: React.ReactNode;
                            let negativeText: string | undefined;
                            if (emptyGroup) {
                                content = isName ? <span className={styles.EmptyGroupText}>{value}</span> : null;
                            } else if (isCircular) {
                                // Circular Txn. — % badge. Customers use the circular fields with the rich
                                // tooltip; summary rows carry a precomputed aggregate % (per the Phase-2 sheet).
                                if (isOverlappingRow(overlapRow)) {
                                    // Own-side % — same denominator as the tooltip's amounts (the
                                    // backend circularPct is min/max-based and diverges when a
                                    // party's other-side amount exceeds its own-side total).
                                    content = (
                                        <PctBadge
                                            pct={circularRevenuePct(overlapRow, tabConfig.totalKey)}
                                            tooltip={
                                                <OverlapTooltipContent row={overlapRow} tabConfig={tabConfig} unitDivisor={unitDivisor} />
                                            }
                                            pill={!summary}
                                            rich
                                            type="circular"
                                        />
                                    );
                                } else if (value != null && value !== '-' && value !== '') {
                                    // Aggregate circular badge (Total B2B / Net B2B rows) — the row already
                                    // carries its base Total and its Net, so Circular ₹ = Total − Net.
                                    const baseTotal = overlapRow[tabConfig.totalKey] as string;
                                    const circAmt = parseINR(baseTotal) - parseINR(overlapRow[NET_KEY] as string);
                                    content = (
                                        <PctBadge
                                            pct={String(value)}
                                            pill
                                            rich
                                            type="circular"
                                            tooltip={
                                                <OverlapTooltipContent
                                                    row={{
                                                        isOverlapping: 'true',
                                                        [tabConfig.totalKey]: baseTotal,
                                                        circularValue: formatWholeRupeesINR(circAmt),
                                                    }}
                                                    tabConfig={tabConfig}
                                                    unitDivisor={unitDivisor}
                                                />
                                            }
                                        />
                                    );
                                } else {
                                    content = '-';
                                }
                            } else if (isNet) {
                                // Net = Total − Circular. Customers with no circular keep their whole Total;
                                // summary rows carry precomputed values (₹ for Total/Net B2B, % for Intra).
                                if (overlapRow.__isIntra || summary || group) {
                                    if (value != null && value !== '-' && value !== '') {
                                        if (overlapRow.__isIntra) {
                                            // Intra share of the circular-netted total (G6) — rich tooltip on badge hover.
                                            content = (
                                                <PctBadge
                                                    pct={String(value)}
                                                    pill
                                                    rich
                                                    type="intra"
                                                    tooltip={
                                                        <IntraTooltipContent
                                                            total={overlapRow.__netBase as string}
                                                            intraAmount={
                                                                (overlapRow.__amounts as Record<string, number> | undefined)?.[
                                                                    tabConfig.totalKey
                                                                ]
                                                            }
                                                            pct={String(value)}
                                                            unitDivisor={unitDivisor}
                                                        />
                                                    }
                                                />
                                            );
                                        } else {
                                            negativeText = value as string;
                                            content = scaleINR(value, unitDivisor) as string;
                                        }
                                    } else {
                                        content = '-';
                                    }
                                } else {
                                    const netText = !isOverlappingRow(overlapRow)
                                        ? (overlapRow[tabConfig.totalKey] as string) || '-'
                                        : getNetValue(overlapRow, tabConfig.totalKey);
                                    negativeText = netText;
                                    content = scaleINR(netText, unitDivisor);
                                }
                            } else if (overlapRow.__isIntra && !isName) {
                                // Intra Company Txn. row — per-month intra share % as a coloured pill.
                                content =
                                    value != null && value !== '-' && value !== '' ? (
                                        <PctBadge
                                            pct={String(value)}
                                            tooltip={
                                                <IntraTooltipContent
                                                    total={(overlapRow.__totalRow as Record<string, string> | undefined)?.[key]}
                                                    intraAmount={(overlapRow.__amounts as Record<string, number> | undefined)?.[key]}
                                                    pct={String(value)}
                                                    unitDivisor={unitDivisor}
                                                />
                                            }
                                            pill
                                            rich
                                            type="intra"
                                        />
                                    ) : (
                                        '-'
                                    );
                            } else if (isName && group) {
                                const expanded = openGroup === overlapRow.__group;
                                content = (
                                    <button
                                        type="button"
                                        className={styles.GroupHeaderContent}
                                        onClick={() => onToggleGroup?.(overlapRow.__group as 'customers' | 'related')}
                                        aria-expanded={expanded}
                                    >
                                        <img
                                            src={dropdownBlack}
                                            alt=""
                                            width="16"
                                            aria-hidden="true"
                                            className={clsx(styles.GroupChevron, { [styles.GroupChevron_Expanded]: expanded })}
                                        />
                                        <span className={styles.GroupTitle}>{value}</span>
                                        <span
                                            className={clsx(styles.GroupBadge, {
                                                [styles.GroupBadge_Customers]: overlapRow.__group === 'customers',
                                                [styles.GroupBadge_Related]: overlapRow.__group === 'related',
                                            })}
                                        >
                                            {overlapRow.__count as number}
                                        </span>
                                    </button>
                                );
                            } else if (isName) {
                                const label = value || '-';
                                const rowTooltip = typeof value === 'string' ? ROW_TOOLTIPS[value] : undefined;
                                if (rowTooltip) {
                                    // Summary rows (Net B2B Revenue, Intra Company Txn.) — label + info icon.
                                    content = (
                                        <span className={styles.NameWithInfo}>
                                            {label}
                                            <Tooltip content={rowTooltip} direction="top" usePortal fitContent>
                                                <img src={overlapInfoIconGrey} alt="info" className={styles.RowInfoIcon} />
                                            </Tooltip>
                                        </span>
                                    );
                                } else if (!summary && value && value !== '-') {
                                    // Customer Name — clamp to 2 lines + ellipsis; full name on hover (per Figma).
                                    // Overlapping rows get a 2nd hover line with the tab's copy + own-side %;
                                    // non-overlapping rows keep the full-name-only hover.
                                    const ownSidePct = isOverlappingRow(overlapRow)
                                        ? circularRevenuePct(overlapRow, tabConfig.totalKey)
                                        : undefined;
                                    const relation = overlapRow.relatedPartyRelation;
                                    const nameHoverLine = relation ? `${value} (${relation})` : value;
                                    const hoverContent =
                                        ownSidePct != null ? (
                                            <>
                                                <div>{nameHoverLine}</div>
                                                <div>
                                                    {tabConfig.nameHoverCopy(
                                                        overlapRow.circularValue as string,
                                                        Number(ownSidePct).toFixed(1),
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            `${nameHoverLine}`
                                        );
                                    const nameWithTooltip = (
                                        <Tooltip content={hoverContent} direction="top" fitContent usePortal>
                                            <span className={styles.CustomerName}>
                                                {label}
                                                {relation && <span className={styles.RelationLabel}> ({relation})</span>}
                                            </span>
                                        </Tooltip>
                                    );
                                    const msmeDelayPan = overlapRow[MSME_DELAY_PAN_KEY];
                                    content =
                                        typeof msmeDelayPan === 'string' ? (
                                            <span className={styles.NameWithMsmeTag}>
                                                {nameWithTooltip}
                                                <MsmeDelayTag pan={msmeDelayPan} vendorName={`${value}`} />
                                            </span>
                                        ) : (
                                            nameWithTooltip
                                        );
                                } else {
                                    content = <span className={styles.CustomerName}>{label}</span>;
                                }
                            } else if (key === 'state' && value !== '-' && value?.length > 25) {
                                content = (
                                    <Tooltip content={`${value}`} direction="bottom" fitContent>
                                        <span style={{ whiteSpace: 'nowrap' }}>{value.substring(0, 25)}...</span>
                                    </Tooltip>
                                );
                            } else {
                                negativeText = value;
                                content = <>{scaleINR(value, unitDivisor) || '-'}</>;
                            }

                            const cellClass = clsx(styles.TableBodyRowItem, {
                                [styles.RightAligned]:
                                    (typeof value === 'string' && value.includes('₹')) || (!isCircular && !isNet && !value && !emptyGroup),
                                [styles.TotalCol]: key === tabConfig.totalKey,
                                [styles.CircularCol]: isCircular,
                                [styles.NetCol]: isNet,
                                [styles.StickyBottom]: summary,
                                [styles.StickyGroup]: group,
                                [styles.GroupValue]: group && !isName,
                                [styles.GroupValue_Expanded]: group && !isName && openGroup === overlapRow.__group,
                                [styles.Negative]: isNegativeAmount(negativeText),
                            });

                            return (
                                <td
                                    className={cellClass}
                                    key={`${header?.name}-${rowKey}`}
                                    style={summary ? { bottom: `${bottomOffset}px` } : groupStyle}
                                >
                                    {content}
                                </td>
                            );
                        })}
                    </tr>
                );
            })}
        </>
    );
}

export default Rows;
